import { useState } from "react";
import { Button } from "~/components/ui/button";
import { Label } from "~/components/ui/label";
import { Checkbox } from "~/components/ui/checkbox";
import { getRoleDisplayName } from "~/utils/role-formatter";
import ReadAboutDeMinimis from "../shared/read-about-de-minimis";
import type { publicApiClient } from "~/lib/orpc-client";

type VerifyTokenResult = Awaited<ReturnType<typeof publicApiClient.collaborators.verifyToken>>;
type Collaborator = NonNullable<VerifyTokenResult["collaborator"]>;
type Application = NonNullable<VerifyTokenResult["application"]>;

type DeclarationItem = {
  role: string;
  text: React.ReactNode;
};

type DeclarationFormProps = {
  collaborator: Collaborator;
  application: Application;
  applicantName: string;
  title: string;
  organizationName?: string;
  contentType?: string | null;
  declarationItems: DeclarationItem[];
  declarationsChecked: Record<string, boolean>;
  onDeclarationChange: (role: string, checked: boolean) => void;
  allDeclarationsChecked: boolean;
  onSubmit: (e: React.FormEvent) => void;
  isSubmitting: boolean;
};

export default function DeclarationForm({
  collaborator,
  application,
  applicantName,
  title,
  organizationName,
  contentType,
  declarationItems,
  declarationsChecked,
  onDeclarationChange,
  allDeclarationsChecked,
  onSubmit,
  isSubmitting,
}: DeclarationFormProps) {
  const [hasReadDeMinimis, setHasReadDeMinimis] = useState(false);

  const rolesDisplay = collaborator.roles
    ?.map((r) => getRoleDisplayName(r as Parameters<typeof getRoleDisplayName>[0]).toLowerCase())
    .join(" and ") ?? "collaborator";

  return (
    <div className="min-h-screen bg-white flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-2xl w-full">
        <div className="bg-white rounded-lg shadow-md p-6 sm:p-8">
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-black mb-2">
              Complete Your {declarationItems.length > 1 ? "Declarations" : "Declaration"}
            </h1>
            <p className="">
              You&apos;ve been invited by <strong>{applicantName}</strong>
              {organizationName && <> from <strong>{organizationName}</strong></>} to
              complete {declarationItems.length > 1 ? "declarations" : "a declaration"} of human origin as the{" "}
              {rolesDisplay} on{" "}
              <strong>{title}.</strong>
            </p>
          </div>

          <form onSubmit={onSubmit} className="space-y-6">
            <div className="space-y-4">
              <p className="text-sm sm:text-base font-bold">
                Before proceeding, you must first{" "}
                <ReadAboutDeMinimis onLinkOpened={() => setHasReadDeMinimis(true)} capitalised={false} completed={hasReadDeMinimis} type="manuscript" />
              </p>

              <div className={hasReadDeMinimis ? "" : "opacity-50 pointer-events-none"}>
                <div className="space-y-4">
                  {declarationItems.map((item) => (
                    <div key={item.role} className="flex space-x-3 sm:space-x-4">
                      <Checkbox
                        id={`ai-usage-declaration-${item.role}`}
                        checked={declarationsChecked[item.role] ?? false}
                        onCheckedChange={(checked) => {
                          onDeclarationChange(item.role, checked === true);
                        }}
                        className="w-4 h-4 sm:w-5 sm:h-5 mt-1"
                      />
                      <Label
                        htmlFor={`ai-usage-declaration-${item.role}`}
                        className="text-sm sm:text-base font-medium cursor-pointer flex-1"
                      >
                        {item.text}
                      </Label>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <Button
              type="submit"
              className="w-full"
              disabled={!hasReadDeMinimis || !allDeclarationsChecked || isSubmitting}
            >
              {isSubmitting
                ? "Submitting..."
                : `Complete ${declarationItems.length > 1 ? "Declarations" : "Declaration"}`}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}
